import { rotulosDoDRE, rotuloDoMes } from '../utils/periodos';
import {
  valoresDaConta,
  quantidadeDePeriodos,
  ultimoIndiceComDado,
  somarConhecidos,
  temMedicao,
  combinar,
  escalar,
  vazia,
  formatarMoeda,
  coberturaDoDRE,
  TRACO
} from '../utils/series';
import React, { useState, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip,
  Alert,
  Button,
  IconButton,
  useTheme
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DownloadIcon from '@mui/icons-material/Download';
import { coresDoGrafico } from '../theme/modernTheme';
import CoberturaChip from './CoberturaChip';
import LineChartCard from './LineChartCard';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { API_ENDPOINTS } from '../config/api';
import { poco } from '../theme/vidro';

/** O nome que o servidor mandou no Content-Disposition, se mandou. */
const nomeDoAnexo = (cabecalho) => {
  const achado = /filename="([^"]+)"/.exec(cabecalho || '');
  return achado ? achado[1] : 'consultoria-dre.xlsx';
};

const DRETab = ({ dreData }) => {
  // Meses reais no lugar de "Mês 1/2/3" — quem lê precisa saber de que período
  // é o número, principalmente ao comparar com a propria planilha.
  const meses = rotulosDoDRE(dreData);
  // A janela de exibição é a que o payload trouxer: três colunas da planilha ou
  // os N meses acumulados pelo fechamento mensal.
  const periodos = quantidadeDePeriodos(dreData);
  const indices = useMemo(() => Array.from({ length: periodos }, (_, i) => i), [periodos]);
  const cobertura = coberturaDoDRE(dreData);
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const arvoreDRE = useMemo(construirArvoreDRE, [dreData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Comeca tudo aberto: o pedido e ver a tabela completa. Colapsar e escolha
  // de quem le, nao estado inicial.
  const idsExpansiveis = useMemo(() => {
    const ids = [];
    arvoreDRE.forEach((grupo) => {
      if (grupo.filhos?.length) ids.push(grupo.id);
      grupo.filhos?.forEach((filho) => {
        if (filho.filhos?.length) ids.push(filho.id);
      });
    });
    return ids;
  }, [arvoreDRE]);

  const [recolhidos, setRecolhidos] = useState(() => new Set());

  const estaAberto = (id) => !recolhidos.has(id);

  const alternar = (id) =>
    setRecolhidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  const tudoAberto = recolhidos.size === 0;
  const alternarTudo = () =>
    setRecolhidos(tudoAberto ? new Set(idsExpansiveis) : new Set());

  // Achata a arvore respeitando o que esta recolhido.
  const linhasVisiveis = [];
  arvoreDRE.forEach((grupo) => {
    linhasVisiveis.push({ ...grupo, nivel: 0 });
    if (!estaAberto(grupo.id)) return;
    (grupo.filhos || []).forEach((filho) => {
      linhasVisiveis.push({ ...filho, nivel: 1 });
      if (!filho.filhos?.length || !estaAberto(filho.id)) return;
      filho.filhos.forEach((neto) => linhasVisiveis.push({ ...neto, nivel: 2 }));
    });
  });

  if (!dreData) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Faça upload da planilha DRE para visualizar os dados
        </Typography>
      </Paper>
    );
  }

  /**
   * Devolve ao usuário o que ele já lançou, no mesmo formato que a importação
   * lê. Corrigir um mês vira "baixa, ajusta a célula, sobe de volta" — e quem
   * preencheu o questionário sai com o arquivo na mão em vez de ficar preso ao
   * nosso banco.
   */
  const baixarPlanilha = async () => {
    setBaixando(true);
    setErroDownload(null);
    try {
      const resposta = await fetch(API_ENDPOINTS.UPLOAD_PLANILHA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dre: dreData })
      });
      if (!resposta.ok) {
        const corpo = await resposta.json().catch(() => ({}));
        throw new Error(corpo.message || 'Não foi possível gerar a planilha.');
      }

      const nome = nomeDoAnexo(resposta.headers.get('Content-Disposition'));
      const url = window.URL.createObjectURL(await resposta.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (erro) {
      setErroDownload(erro.message);
    } finally {
      setBaixando(false);
    }
  };

  // Preparar dados para tabela DRE resumida
  /**
   * Monta o DRE como arvore: grupo -> (subgrupo) -> conta.
   *
   * Os totais dos grupos vem sempre dos totalizadores ja calculados
   * (indicadores / bucket.total) e NUNCA sao recalculados a partir das contas:
   * sao os numeros conferidos contra a planilha. As contas apenas detalham.
   * Quando a soma das contas nao alcanca o total do grupo, entra uma linha
   * residual explicita em vez de maquiar a diferenca.
   *
   * Lacuna e zero sao coisas diferentes em toda esta funcao: mes nao informado
   * fica `null` e a celula sai como "—". Zero e uma medicao.
   */
  function construirArvoreDRE() {
    if (!dreData || !dreData.indicadores || !periodos) return [];

    const serieDoIndicador = (valores) =>
      Array.isArray(valores) ? valoresDaConta(valores, periodos) : vazia(periodos);

    const faturamento = serieDoIndicador(dreData.indicadores.faturamento?.valores);
    const custosVariaveis = serieDoIndicador(dreData.indicadores.custosVariaveis?.valores);
    const custosFixos = serieDoIndicador(dreData.indicadores.custosFixos?.valores);
    const lucroReal = serieDoIndicador(dreData.indicadores.lucro?.real?.valores);

    /** Contas de um bucket { nome: folha, total: [] }, sem as vazias. */
    const contasDe = (bucket, sinal = 1) =>
      Object.entries(bucket || {})
        .filter(([chave]) => chave !== 'total')
        // A chave do bucket e o CODIGO da conta; o rotulo mora em `conta.nome`.
        // Duas contas homonimas (o aluguel de duas lojas) colidiam quando a
        // chave era o nome, e uma sumia da tabela. O `??` mantem legivel o
        // snapshot antigo, gravado quando a chave era o proprio nome.
        .map(([chave, conta]) => ({
          nome: conta?.nome ?? chave,
          valores: escalar(valoresDaConta(conta, periodos), sinal)
        }))
        .filter((c) => temMedicao(c.valores))
        .sort((a, b) => Math.abs(b.valores[0] ?? 0) - Math.abs(a.valores[0] ?? 0));

    /**
     * Diferenca entre o total do grupo e a soma das contas detalhadas.
     *
     * Mes sem total do grupo nao tem residual — nao da para dizer quanto ficou
     * de fora de um numero que ninguem informou.
     */
    const residual = (totalGrupo, contas, sinal = 1) => {
      const serieTotal = serieDoIndicador(totalGrupo);
      const dif = indices.map((i) => {
        if (serieTotal[i] === null) return null;
        const soma = somarConhecidos(contas.map((c) => c.valores[i])) ?? 0;
        return serieTotal[i] * sinal - soma;
      });
      return temMedicao(dif)
        ? [{ nome: 'Não detalhado na planilha', valores: dif, residual: true }]
        : [];
    };

    const filhosDe = (bucket, totalGrupo, sinal = 1) => {
      const contas = contasDe(bucket, sinal);
      return [...contas, ...residual(totalGrupo, contas, sinal)];
    };

    const linhas = [];

    // --- Receita operacional
    const contasReceita = filhosDe(dreData.receitas?.operacionais, faturamento);
    linhas.push({
      id: 'receita',
      descricao: 'RECEITA OPERACIONAL BRUTA',
      valores: faturamento,
      tipo: 'receita',
      filhos: contasReceita.map((c, i) => ({ id: `receita-${i}`, ...c, descricao: c.nome, tipo: 'conta' }))
    });

    // --- Receita nao operacional: informativa, fora da conta do lucro
    const contasNaoOp = contasDe(dreData.receitas?.naoOperacionais);
    if (contasNaoOp.length) {
      const totalNaoOp = indices.map((i) => somarConhecidos(contasNaoOp.map((c) => c.valores[i])));
      linhas.push({
        id: 'receita-nao-op',
        descricao: 'RECEITAS NÃO OPERACIONAIS',
        valores: totalNaoOp,
        tipo: 'informativo',
        nota: 'Não entra no lucro operacional',
        filhos: contasNaoOp.map((c, i) => ({ id: `nao-op-${i}`, ...c, descricao: c.nome, tipo: 'conta' }))
      });
    }

    // --- Custos variaveis (exibidos negativos, como o grupo)
    const contasVariaveis = filhosDe(dreData.despesas?.custosVariaveis, custosVariaveis, -1);
    linhas.push({
      id: 'custos-variaveis',
      descricao: '(-) CUSTOS VARIÁVEIS',
      valores: escalar(custosVariaveis, -1),
      tipo: 'custo',
      filhos: contasVariaveis.map((c, i) => ({ id: `cv-${i}`, ...c, descricao: c.nome, tipo: 'conta' }))
    });

    linhas.push({
      id: 'lucro-bruto',
      descricao: '(=) LUCRO BRUTO',
      valores: combinar(faturamento, custosVariaveis, (fat, cv) => fat - cv),
      tipo: 'subtotal',
      filhos: []
    });

    // --- Custos fixos: um nivel a mais, por natureza de despesa
    const SUBGRUPOS = [
      { chave: 'pessoal', rotulo: 'Despesas com pessoal' },
      { chave: 'socios', rotulo: 'Despesas com sócios' },
      { chave: 'administrativas', rotulo: 'Despesas administrativas' },
      { chave: 'financeiras', rotulo: 'Despesas financeiras' },
      { chave: 'marketing', rotulo: 'Despesas com marketing' }
    ];

    const buckets = dreData.despesas?.custosFixos || {};
    const subgrupos = SUBGRUPOS.map(({ chave, rotulo }) => {
      const bucket = buckets[chave] || {};
      const contas = filhosDe(bucket, bucket.total, -1);
      return {
        id: `cf-${chave}`,
        descricao: rotulo,
        valores: escalar(serieDoIndicador(bucket.total), -1),
        tipo: 'subgrupo',
        filhos: contas.map((c, i) => ({ id: `cf-${chave}-${i}`, ...c, descricao: c.nome, tipo: 'conta' }))
      };
    }).filter((sg) => temMedicao(sg.valores) || sg.filhos.length);

    linhas.push({
      id: 'custos-fixos',
      descricao: '(-) CUSTOS FIXOS',
      valores: escalar(custosFixos, -1),
      tipo: 'custo',
      filhos: [...subgrupos, ...residual(custosFixos, subgrupos, -1).map((r, i) => ({
        id: `cf-residual-${i}`,
        ...r,
        descricao: r.nome,
        tipo: 'conta'
      }))]
    });

    linhas.push({
      id: 'lucro-liquido',
      descricao: '(=) LUCRO LÍQUIDO',
      valores: lucroReal,
      tipo: 'total',
      filhos: []
    });

    return linhas;
  }

  /** Contas de receita operacional com a série completa da janela. */
  const prepareReceitasDetalhadas = () => {
    const operacionais = dreData.receitas?.operacionais;
    if (!operacionais) return [];

    return Object.keys(operacionais)
      .filter((chave) => chave !== 'total' && operacionais[chave] && typeof operacionais[chave] === 'object')
      .map((chave) => ({
        descricao: operacionais[chave]?.nome ?? chave,
        valores: valoresDaConta(operacionais[chave], periodos)
      }))
      .filter((linha) => linha.valores.length > 0);
  };

  /**
   * Composicao dos custos fixos no ULTIMO mes com dado.
   *
   * Antes era sempre o primeiro mes da janela: com tres colunas dava na mesma,
   * com doze mostraria a composicao de um ano atras. O rotulo nomeia o mes.
   */
  const indiceDaPizza = ultimoIndiceComDado(
    Array.isArray(dreData.indicadores?.custosFixos?.valores)
      ? dreData.indicadores.custosFixos.valores
      : []
  );

  const prepareCostPieData = () => {
    const custosFixos = dreData.despesas?.custosFixos;
    if (!custosFixos || indiceDaPizza === -1) return [];

    const fatia = (chave) => valoresDaConta(custosFixos[chave]?.total, periodos)[indiceDaPizza];

    return [
      { name: 'Pessoal', value: fatia('pessoal') },
      { name: 'Sócios', value: fatia('socios') },
      { name: 'Administrativas', value: fatia('administrativas') },
      { name: 'Marketing', value: fatia('marketing') },
      { name: 'Financeiras', value: fatia('financeiras') }
    ].filter((item) => item.value !== null && item.value > 0);
  };

  /**
   * Evolucao das margens.
   *
   * Mes sem faturamento informado devolve `null`, e nao zero: o grafico corta a
   * linha ali (`connectNulls={false}`) em vez de desenhar uma queda a 0% que
   * ninguem mediu.
   */
  const prepareMargensChart = () => {
    if (!dreData.indicadores) return [];

    const faturamento = valoresDaConta(dreData.indicadores.faturamento?.valores, periodos);
    const custosVariaveis = valoresDaConta(dreData.indicadores.custosVariaveis?.valores, periodos);
    const lucro = valoresDaConta(dreData.indicadores.lucro?.real?.valores, periodos);

    const margem = (numerador, i) => {
      const fat = faturamento[i];
      if (fat === null || !(fat > 0) || numerador === null) return null;
      return (numerador / fat) * 100;
    };

    return meses.map((month, i) => ({
      month,
      'Margem Bruta': margem(
        custosVariaveis[i] === null || faturamento[i] === null ? null : faturamento[i] - custosVariaveis[i],
        i
      ),
      'Margem Líquida': margem(lucro[i], i)
    }));
  };

  // Paleta do tema: a pizza de custos usava azul/verde/amarelo/laranja
  // saturados, destoando do resto do painel depois da revisão visual. E ela era
  // constante de módulo, então no tema escuro a legenda saía em cor pensada
  // para fundo branco — ilegível.
  const CORES_GRAFICO = coresDoGrafico(theme.palette.mode === 'dark');
  const COLORS = [
    CORES_GRAFICO.primaria,
    CORES_GRAFICO.secundaria,
    CORES_GRAFICO.terciaria,
    CORES_GRAFICO.atencao,
    CORES_GRAFICO.negativa
  ];

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: isDark ? '#1e293b' : 'white',
          padding: '10px',
          border: `1px solid ${isDark ? '#334155' : '#ccc'}`,
          borderRadius: '4px',
          color: isDark ? '#f1f5f9' : '#1e293b'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{payload[0].name}</p>
          <p style={{ margin: '5px 0', color: payload[0].payload.fill }}>
            {formatarMoeda(payload[0].value)}
          </p>
          <p style={{ margin: 0, fontSize: '12px', color: theme.palette.text.secondary }}>
            {Number((payload[0].value / payload[0].payload.total) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
          </p>
        </div>
      );
    }
    return null;
  };

  const receitasDetalhadas = prepareReceitasDetalhadas();
  const costPieData = prepareCostPieData();
  const margensChart = prepareMargensChart();
  const pontoEquilibrioSerie = valoresDaConta(dreData.indicadores?.pontoEquilibrio?.valores, periodos);
  const faturamentoSerie = valoresDaConta(dreData.indicadores?.faturamento?.valores, periodos);

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 3 }}>
        Demonstração do Resultado do Exercício (DRE)
      </Typography>

      {/* DRE detalhada, com grupos expansiveis */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap'
              }}
            >
              <Box>
                <Typography variant="h6">Demonstrativo detalhado</Typography>
                <Typography variant="caption" color="text.secondary">
                  Clique num grupo para abrir ou fechar o detalhamento das contas
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CoberturaChip cobertura={cobertura} />
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={baixarPlanilha}
                  disabled={baixando || !periodos}
                >
                  {baixando ? 'Gerando...' : 'Baixar planilha'}
                </Button>
                <Button size="small" onClick={alternarTudo} disabled={!idsExpansiveis.length}>
                  {tudoAberto ? 'Recolher tudo' : 'Expandir tudo'}
                </Button>
              </Box>
            </Box>
            {erroDownload && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroDownload(null)}>
                {erroDownload}
              </Alert>
            )}
            {/* A ROLAGEM HORIZONTAL É DA TABELA, NUNCA DA PÁGINA.
                Medido a 390px: o corpo do documento tinha 768px de largura —
                quase o dobro da viewport. Quem lê no celular arrastava a página
                inteira de lado e a coluna DESCRIÇÃO saía da tela, deixando
                colunas de números sem rótulo nenhum. Com a largura presa ao
                contêiner, a página fica quieta e só a grade desliza; a primeira
                coluna gruda para o rótulo nunca sumir. */}
            {/* O demonstrativo é a maior superfície de DADO do produto: ele vai
                para o poço, como as listas de indicador. */}
            <TableContainer
              sx={(t) => ({
                ...poco(t.palette.mode === 'dark'),
                px: { xs: 1, sm: 1.5 },
                maxWidth: '100%',
                overflowX: 'auto'
              })}
            >
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <strong>Descrição</strong>
                    </TableCell>
                    {meses.map((mes) => (
                      <TableCell key={mes} align="right"><strong>{mes}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhasVisiveis.map((row) => {
                    const ehGrupo = row.nivel === 0;
                    const destaque = row.tipo === 'total' || row.tipo === 'subtotal';
                    const podeAbrir = Boolean(row.filhos?.length);
                    const aberto = estaAberto(row.id);

                    // Lacuna não é boa nem má notícia: é ausência. Sai cinza,
                    // com o mesmo tom do rótulo secundário.
                    //
                    // `text.disabled` media 1,47:1 — o travessão, que é o
                    // símbolo central deste produto, era o texto MENOS legível
                    // da tela. Ausência tem de ser LIDA: linha que parece vazia
                    // faz o usuário concluir que o sistema quebrou, e não que
                    // falta dado dele.
                    const corDoValor = (valor) => {
                      if (valor === null) return 'text.secondary';
                      if (row.tipo === 'total') return valor >= 0 ? 'success.main' : 'error.main';
                      if (row.tipo === 'subtotal') return valor >= 0 ? 'text.primary' : 'error.main';
                      if (row.residual) return 'text.secondary';
                      return valor < 0 ? 'error.main' : 'text.primary';
                    };

                    return (
                      <TableRow
                        key={row.id}
                        hover={podeAbrir}
                        onClick={podeAbrir ? () => alternar(row.id) : undefined}
                        sx={{
                          cursor: podeAbrir ? 'pointer' : 'default',
                          bgcolor: row.tipo === 'total' ? 'action.hover' : 'inherit',
                          '& td': {
                            borderBottom: ehGrupo ? undefined : 'none',
                            py: ehGrupo ? 1.25 : 0.5
                          }
                        }}
                      >
                        <TableCell
                          sx={{
                            pl: 2 + row.nivel * 3,
                            fontWeight: destaque || ehGrupo ? 600 : 400,
                            color: row.residual ? 'text.secondary' : 'text.primary',
                            fontStyle: row.residual ? 'italic' : 'normal',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {podeAbrir ? (
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.25,
                                  // Media 24x24px, contra o mínimo de 44 — e é
                                  // a única affordance visível de abrir o grupo
                                  // do DRE. Mesma técnica da régua: o alvo
                                  // cresce por pseudo-elemento, não por
                                  // padding, porque engordar a linha custaria
                                  // altura numa tabela que já é densa e que no
                                  // celular disputa cada dobra.
                                  position: 'relative',
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: 44,
                                    height: 44,
                                    transform: 'translate(-50%, -50%)'
                                  }
                                }}
                                aria-label={aberto ? 'Recolher' : 'Expandir'}
                              >
                                {aberto ? (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 22 }} />
                            )}
                            {row.descricao}
                            {row.nota && (
                              <Chip
                                label={row.nota}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        {row.valores.map((valor, i) => (
                          <TableCell
                            key={i}
                            align="right"
                            sx={{
                              color: corDoValor(valor),
                              fontWeight: destaque ? 700 : ehGrupo ? 600 : 400,
                              fontVariantNumeric: 'tabular-nums',
                              fontStyle: row.residual ? 'italic' : 'normal',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatarMoeda(valor)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos de Análise */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <LineChartCard
            title="Evolução das Margens (%)"
            data={margensChart}
            lines={[
              { dataKey: 'Margem Bruta', color: CORES_GRAFICO.primaria, name: 'Margem Bruta', format: 'percentage' },
              { dataKey: 'Margem Líquida', color: CORES_GRAFICO.secundaria, name: 'Margem Líquida', format: 'percentage' }
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Composição de Custos Fixos ({rotuloDoMes(dreData, indiceDaPizza)})
            </Typography>
            {/* CARTÃO VAZIO NÃO É ESTADO: É TELA QUEBRADA.
                Sem o total por grupo de custo fixo, a pizza não tem fatia e o
                cartão renderizava um retângulo branco de 300px sob um título —
                o usuário conclui que o produto travou, e não que falta dado.
                A ausência aqui tem de dizer o nome dela, como no resto do
                produto. */}
            {costPieData.length === 0 ? (
              <Box
                sx={{
                  height: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1,
                  px: 2
                }}
              >
                <Typography variant="h6" component="p" color="text.secondary">
                  {TRACO}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '38ch' }}>
                  Requer o total por grupo de custo fixo (pessoal, sócios, administrativas,
                  marketing e financeiras) no mês exibido.
                </Typography>
              </Box>
            ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={costPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${Number(percent * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  // MESMA REGRA DO GRÁFICO DE LINHA. O recharts reanima a cada
                  // mudança de layout, e a pizza a meio caminho é uma lasca:
                  // medido, a composição de custos fixos saía como um triângulo
                  // fino com quatro rótulos flutuando em volta. Composição que
                  // se redesenha sozinha parece dado chegando; aqui já chegou.
                  isAnimationActive={false}
                >
                  {costPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
                {/* Mesma regra do gráfico de linha: quem identifica a fatia é o
                    quadradinho colorido, e o nome dela é texto — a 2,59:1, a
                    cor da fatia não se lia como rótulo. */}
                <Legend
                  formatter={(valor) => (
                    <span style={{ color: theme.palette.text.secondary }}>{valor}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Receitas Detalhadas */}
      {receitasDetalhadas.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Receitas Detalhadas por Forma de Pagamento
              </Typography>
              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Forma de Pagamento</strong></TableCell>
                      {meses.map((mes) => (
                        <TableCell key={mes} align="right"><strong>{mes}</strong></TableCell>
                      ))}
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receitasDetalhadas.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.descricao}</TableCell>
                        {row.valores.map((valor, i) => (
                          <TableCell key={i} align="right">{formatarMoeda(valor)}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {/* Soma o que foi informado. Mês em branco não vira zero
                              no total — ele simplesmente não entra. */}
                          {formatarMoeda(somarConhecidos(row.valores))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Análise de Ponto de Equilíbrio */}
      {dreData.indicadores && dreData.indicadores.pontoEquilibrio && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Análise de Ponto de Equilíbrio
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {indices.map((index) => {
                    const pe = pontoEquilibrioSerie[index];
                    const faturamento = faturamentoSerie[index];
                    // Sem um dos dois nao ha veredito: dizer "nao atingido" para
                    // um mes que ninguem lancou seria acusar de prejuizo quem so
                    // nao informou o mes.
                    const comparavel = pe !== null && faturamento !== null;
                    const atingiu = comparavel && faturamento >= pe;
                    const margem = comparavel && faturamento > 0
                      ? ((faturamento - pe) / faturamento) * 100
                      : null;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {meses[index] || `Mês ${index + 1}`}
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 1 }}>
                            {formatarMoeda(pe)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ponto de Equilíbrio
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">
                              Faturamento: {formatarMoeda(faturamento)}
                            </Typography>
                            <Chip
                              label={comparavel ? (atingiu ? 'Atingido' : 'Não Atingido') : 'Mês não informado'}
                              color={comparavel ? (atingiu ? 'success' : 'error') : 'default'}
                              variant={comparavel ? 'filled' : 'outlined'}
                              size="small"
                            />
                          </Box>
                          {atingiu && margem !== null && (
                            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                              Margem de Segurança: {Number(margem).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DRETab;
